import * as React from "react";
import type { JSX } from "react";

import { RowCountLabelVariant } from "@impero-com/ui";
import {
    ColumnProps,
    TablePaginationConfig as PaginationConfig,
} from "antd/es/table";
import type {
    FilterValue,
    RowSelectMethod,
    SorterResult,
} from "antd/es/table/interface";
import memoizeOne from "memoize-one";
import { GetComponentProps, TableComponents } from "rc-table/es/interface";

import * as DV from "../../utils/dataView";
import { assertNever, set } from "../../utils/obj";
import {
    RowClickHandler,
    ActiveRowClickHandler,
    SimpleRowClickHandler,
    RowNavigation,
} from "../../utils/rowClickHandler";
import { nullToEmpty } from "../../utils/string";
import { PaginatedTable } from "../table/paginatedTable";

import { toColumn, sortColumns } from "./columns";
import type {
    FormattedView,
    ColumnBuilder,
    FixedWidthLayout,
    Layout,
    Display,
    CellClassName,
    ColumnOptions,
    SimpleColumn,
} from "./columns";
import * as theme from "./theme.css";
import type {
    SelectionHandler,
    SelectionToolSpec,
    StandardToolSpec,
    SelectionTools,
} from "./toolbar";
import {
    buildStandardTools,
    buildSelectionExportTool,
    buildStandardExportTool,
    buildToolbarWithViewAdjuster,
    DataTableToolbar,
} from "./toolbar";

import "antd-table-infinity/index.css";

const DEFAULT_COLUMN_BUILDER_ID = "__DEFAULT__";
const ID_FIELD = "__ID";
const ITEM_FIELD = "__ITEM";

// This component has the same behaviour as the default cell component.
// The only thing it does is the aggregation of 2 props to form the ultimate className prop.
// This is not a lot of additional CPU as the default cell component is "td".
class CellComponent<
    P extends { className?: string },
> extends React.PureComponent<P & { additionalClassName?: string }> {
    render = () => {
        const { additionalClassName, ...otherProps } = this.props;
        const tdProps = {
            ...otherProps,
            className: `${nullToEmpty(otherProps.className)} ${nullToEmpty(
                additionalClassName
            )}`,
        };
        return <td {...tdProps} />;
    };
}

class DataTable<ITEM> extends React.PureComponent<
    DataTableProps<ITEM>,
    DataTableState
> {
    constructor(props: DataTableProps<ITEM>) {
        super(props);
        this.state = {
            sorter: undefined,
            selectedRowIds: [],
            visibleColumnIds: props.visibleColumnIds || new Set(),
        };
    }

    render = () => {
        const {
            id,
            dataView,
            columnBuilders,
            mode,
            rowClickHandler,
            expandedRowRender,
            footerRenderer,
            hidePlaceholder,
            expandedRowClassName,
            getRowCountLabel,
            getSinglePageRowCountLabel,
        } = this.props;
        const { visibleColumnIds } = this.state;
        const formattedView = this.formattedView(dataView, columnBuilders);
        const sorter = this.sorter(this.state.sorter, dataView);
        const dataSource = this.dataSource(formattedView, sorter);
        const getRowClassName = this.getRowClassName(this.getActiveRowId());
        let handleOnRow: GetComponentProps<any> | undefined;
        if (rowClickHandler !== undefined) {
            handleOnRow = this.handleOnRow(dataSource);
        } else {
            handleOnRow = undefined;
        }
        const actualExpandedRowRender = expandedRowRender
            ? this.expandRow
            : undefined;
        const actualExpandedRowClassName = expandedRowClassName
            ? this.getExpandedRowClassName
            : undefined;

        let noSpaceAfterLastRow: "noSpaceAfterLastRow" | "";
        if (mode === undefined) {
            noSpaceAfterLastRow = "noSpaceAfterLastRow";
        } else {
            switch (mode.type) {
                case "MAX_HEIGHT":
                    noSpaceAfterLastRow = "noSpaceAfterLastRow";
                    break;
                case "VIEWPORT":
                    noSpaceAfterLastRow = "";
                    break;
                default:
                    return assertNever(mode);
            }
        }
        const className = `${theme.dataTable} ${
            hidePlaceholder ? theme.hidePlaceholder : ""
        } ${noSpaceAfterLastRow}`;

        return (
            <>
                {this.buildToolbar()}
                <PaginatedTable
                    id={id}
                    dataSource={dataSource}
                    getRowCountLabel={getRowCountLabel}
                    getSinglePageRowCountLabel={getSinglePageRowCountLabel}
                    components={this.tableComponents(footerRenderer)}
                    rowSelection={this.rowSelection(
                        this.props.selectionHandler,
                        this.state.selectedRowIds
                    )}
                    className={className}
                    columns={this.antdColumns(
                        formattedView,
                        visibleColumnIds,
                        sorter
                    )}
                    rowKey={ID_FIELD}
                    scroll={this.scroll(mode)}
                    bordered
                    pagination={false}
                    rowClassName={getRowClassName}
                    expandedRowRender={actualExpandedRowRender}
                    expandedRowClassName={actualExpandedRowClassName}
                    onRow={handleOnRow}
                    onChange={this.handleTableChange}
                    showSorterTooltip={false}
                    paginationVariant={this.props.paginationVariant}
                />
            </>
        );
    };

    componentDidMount = () => {
        // We need to update the height of the table when the viewport resizes
        window.addEventListener("resize", this.updateStyle);

        // We need to update the maxHeight AFTER the first render has completed, to know how much height is left
        window.requestAnimationFrame(() => {
            // We set multiple timers because firefox calls the requestAnimationFrame callback before everything is rendered and depending on the pc we run on the page finished rendering at different times
            // TODO: Is there a better way to do this when requestAnimationFrame is unreliable?
            setTimeout(() => {
                this.updateStyle();
            });
            setTimeout(() => {
                this.updateStyle();
            }, 500);
            setTimeout(() => {
                this.updateStyle();
            }, 1000);
            setTimeout(() => {
                this.updateStyle();
            }, 2000);
        });
    };

    componentDidUpdate = () => {
        this.updateStyle();
        this.ensureSelectionHasValidIds();
    };

    componentWillUnmount = () => {
        window.removeEventListener("resize", this.updateStyle);
    };

    tableComponents = memoizeOne(
        (footerRenderer: (() => JSX.Element) | undefined) => {
            const components: TableComponents<unknown> = {
                body: {
                    cell: CellComponent,
                },
            };
            if (footerRenderer) {
                components.body = set(components.body, {
                    wrapper: this.renderFooter(),
                });
            }
            return components;
        }
    );

    private renderFooter = () => {
        const { footerRenderer } = this.props;
        const bodyWrapper = (props: any) => {
            return (
                <tbody {...props}>
                    {props.children}
                    {footerRenderer!()}
                </tbody>
            );
        };
        return bodyWrapper;
    };

    private buildToolbar = () => {
        const {
            dataView,
            columnBuilders,
            selectionHandler,
            tools,
            expandedRowRender,
        } = this.props;
        const { visibleColumnIds } = this.state;
        const formattedView = this.formattedView(dataView, columnBuilders);
        let selectionTools: SelectionTools<ITEM> | undefined;
        if (selectionHandler) {
            const { tools } = selectionHandler;
            if (tools) {
                const { selectedRowIds } = this.state;
                selectionTools = {
                    selectedRowIds,
                    tools,
                    onSelectionActionDone: this.resetSelection,
                };
            } else {
                selectionTools = undefined;
            }
        } else {
            selectionTools = undefined;
        }
        if (!selectionTools && !tools) {
            return null;
        }

        return (
            <DataTableToolbar<ITEM>
                formattedView={formattedView}
                selectionTools={selectionTools}
                visibleColumnIds={visibleColumnIds}
                tools={tools}
                hasExpansionColumn={!!expandedRowRender}
                onVisibleColumnsChanged={this.handleVisibleColumnsChanged}
            />
        );
    };

    // AntD documentation is not clear about this. But it transpires that the window inner size might be a good choice.
    // If not set, the infinity table appears with a grey background instead of white. There might be other side effects.
    private scroll = memoizeOne((mode: DataTableMode | undefined) =>
        mode !== undefined ? { x: 0, y: window.innerHeight } : undefined
    );

    private formattedView = memoizeOne(
        (
            dataView: DV.DataView<ITEM>,
            columnBuilders:
                | { [extractorId: string]: ColumnBuilder<ITEM, any> }
                | undefined
        ) => DataTable.buildFormattedView(dataView, columnBuilders)
    );
    private unsortedDataSource = memoizeOne(
        (formattedView: FormattedView<ITEM>) =>
            this.buildUnsortedDataSource(formattedView)
    );
    private dataSource = memoizeOne(
        (formattedView: FormattedView<ITEM>, sorter: DV.Sorter | undefined) =>
            this.buildDataSource(formattedView, sorter)
    );
    private allIds = memoizeOne((formattedView: FormattedView<ITEM>) =>
        this.buildAllIds(formattedView)
    );
    private antdColumns = memoizeOne(
        (
            formattedView: FormattedView<ITEM>,
            visibleColumnIds: Set<string>,
            sorter: DV.Sorter | undefined
        ) => this.buildAntdColumns(formattedView, visibleColumnIds, sorter)
    );
    private sorter = memoizeOne(
        (sorter: DV.Sorter | undefined, dataView: DV.DataView<ITEM>) => {
            if (sorter !== undefined) {
                return sorter;
            } else if (dataView.sorters.length > 0) {
                return dataView.sorters[0];
            } else {
                return undefined;
            }
        }
    );

    static getDerivedStateFromProps = <ITEM extends object>(
        props: DataTableProps<ITEM>,
        state: DataTableState
    ) => {
        if (props.visibleColumnIds) {
            return { visibleColumnIds: props.visibleColumnIds };
        } else {
            const { visibleColumnIds } = state;
            const { dataView, columnBuilders } = props;
            const formattedView = DataTable.buildFormattedView(
                dataView,
                columnBuilders
            );
            return {
                visibleColumnIds: getVisibleColumnsIds(
                    formattedView,
                    visibleColumnIds
                ),
            };
        }
    };

    private getSelectableRowIds = (
        rowIds: string[],
        canSelect: (item: ITEM) => boolean
    ) => {
        const { dataView, columnBuilders } = this.props;
        const formattedView = this.formattedView(dataView, columnBuilders);

        return rowIds.filter((rowId: string) => {
            const row = this.unsortedDataSource(formattedView).find(
                (rowItem: any) => rowItem[ID_FIELD] === rowId
            ) as any;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            return row !== undefined && canSelect?.(row[ITEM_FIELD]);
        });
    };

    private rowSelection = memoizeOne(
        (
            selectionHandler: SelectionHandler<ITEM> | undefined,
            selectedRowIds: Array<string>
        ) => {
            if (!selectionHandler) {
                return undefined;
            }

            return {
                selectedRowKeys: selectionHandler.canSelect
                    ? this.getSelectableRowIds(
                          selectedRowIds,
                          selectionHandler.canSelect
                      )
                    : selectedRowIds,
                onChange: (
                    newSelectedRowIds: React.Key[],
                    _rows: ITEM[],
                    info: { type: RowSelectMethod }
                ) => {
                    switch (info.type) {
                        case "all":
                            const { dataView, columnBuilders } = this.props;
                            const formattedView = this.formattedView(
                                dataView,
                                columnBuilders
                            );
                            const allNewSelectedRowIds =
                                this.allIds(formattedView);
                            const allRowIds = selectionHandler.canSelect
                                ? this.getSelectableRowIds(
                                      allNewSelectedRowIds,
                                      selectionHandler.canSelect
                                  )
                                : allNewSelectedRowIds;
                            if (selectionHandler.onSelectionChange) {
                                selectionHandler.onSelectionChange(
                                    newSelectedRowIds as Array<string>
                                );
                            }
                            this.setState({
                                // because "all" is called when "unselecting all" also
                                selectedRowIds:
                                    this.state.selectedRowIds.length ===
                                    allRowIds.length
                                        ? []
                                        : allRowIds,
                            });
                            break;
                        case "single":
                        case "multiple":
                            this.setState({
                                selectedRowIds:
                                    newSelectedRowIds as Array<string>,
                            });
                            if (selectionHandler.onSelectionChange) {
                                selectionHandler.onSelectionChange(
                                    newSelectedRowIds as Array<string>
                                );
                            }
                            break;
                        case "invert":
                        case "none":
                            break;
                        default:
                            assertNever(info.type);
                    }
                },
                getCheckboxProps: (record: any) => {
                    const selectable =
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                        selectionHandler.canSelect?.(record[ITEM_FIELD]) ===
                        false;
                    return {
                        disabled: selectable,
                        style: selectable ? { display: "none" } : undefined,
                    };
                },
                preserveSelectedRowKeys: true,
            };
        }
    );

    private static buildFormattedView = <ITEM,>(
        dataView: DV.DataView<ITEM>,
        columnBuilders:
            | { [extractorId: string]: ColumnBuilder<ITEM, unknown> }
            | undefined
    ) => {
        const allColumns = dataView.extractorGroups.reduce(
            (memo, group) => {
                return memo.concat(
                    group.extractors.map((extractor) => {
                        const builder =
                            (columnBuilders &&
                                (columnBuilders[extractor.id] ||
                                    columnBuilders[
                                        DEFAULT_COLUMN_BUILDER_ID
                                    ])) ||
                            undefined;
                        const column = toColumn(
                            extractor,
                            builder && builder(extractor)
                        );
                        return column;
                    })
                );
            },
            [] as Array<SimpleColumn<ITEM>>
        );
        sortColumns(allColumns);
        const formattedView: FormattedView<ITEM> = {
            dataView,
            columns: allColumns,
        };
        return formattedView;
    };

    private buildAntdColumns = (
        formattedView: FormattedView<ITEM>,
        visibleColumnIds: Set<string>,
        sorter: DV.Sorter | undefined
    ) => {
        const antdColumns: Array<ColumnProps<any>> = formattedView.columns
            .filter((col) => visibleColumnIds.has(col.extractor.id))
            .map((col) => {
                const { layout } = col;
                let width: number | undefined;
                if (layout) {
                    width = layout.fixedWidth;
                }
                const { extractor, cellClassName } = col;
                let onCell:
                    | ((record: any, rowIndex: number | undefined) => any)
                    | undefined;
                if (!cellClassName) {
                    onCell = undefined;
                } else {
                    switch (cellClassName.type) {
                        case "Static": {
                            onCell = () => ({
                                additionalClassName: cellClassName.value,
                            });
                            break;
                        }
                        case "Dynamic": {
                            onCell = (record, rowIndex) => {
                                if (rowIndex === undefined) {
                                    throw Error("rowIndex is undefined");
                                } // Should never occur
                                return {
                                    additionalClassName: cellClassName.get(
                                        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                                        record[ITEM_FIELD],
                                        rowIndex
                                    ),
                                };
                            };
                            break;
                        }
                        default: {
                            return assertNever(cellClassName);
                        }
                    }
                }
                let sortOrder: "ascend" | "descend" | undefined;
                if (sorter && sorter.extractorId === extractor.id) {
                    sortOrder =
                        sorter.direction === "ASC" ? "ascend" : "descend";
                }
                const antdColumn: ColumnProps<any> = {
                    title: extractor.label,
                    key: extractor.id,
                    dataIndex: extractor.id,

                    render: (_value: any, row: any) => {
                        const item = row[ITEM_FIELD];
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                        return col.getFormattedValue(item);
                    },
                    className: theme.header,
                    onCell,
                    sorter: col.sortable,
                    sortOrder,
                    width,
                };
                return antdColumn;
            });
        return antdColumns;
    };

    private buildUnsortedDataSource = (formattedView: FormattedView<ITEM>) => {
        const { dataView } = formattedView;
        const iter = dataView.dataSource.iter();
        const rows: Array<object> = [];
        while (true) {
            const item = iter.next();
            if (item === undefined) {
                break;
            }
            const row = this.row(item, formattedView);
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            rows.push(row);
        }
        return rows;
    };
    private currentDataSource: Array<any> | null = null;
    private buildDataSource = (
        formattedView: FormattedView<ITEM>,
        sorter: DV.Sorter | undefined
    ) => {
        let dataSource = this.unsortedDataSource(formattedView);
        const { dataView } = formattedView;
        const defaultSorter = this.getDefaultSorter(formattedView.dataView);
        if (
            sorter !== undefined &&
            (defaultSorter === undefined ||
                defaultSorter.extractorId !== sorter.extractorId ||
                defaultSorter.direction !== sorter.direction)
        ) {
            dataSource = dataSource.slice();
            const extractor = DV.getExtractor(dataView, sorter.extractorId);
            dataSource.sort((row1: any, row2: any) => {
                let rowCompare1: any;
                let rowCompare2: any;
                switch (sorter.direction) {
                    case "ASC":
                        rowCompare1 = row1;
                        rowCompare2 = row2;
                        break;
                    case "DESC":
                        rowCompare1 = row2;
                        rowCompare2 = row1;
                        break;
                    default:
                        assertNever(sorter.direction);
                }
                return extractor.compare(
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                    rowCompare1[ITEM_FIELD],
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                    rowCompare2[ITEM_FIELD]
                );
            });
        }
        this.currentDataSource = dataSource;
        return dataSource;
    };
    private buildAllIds = (
        formattedView: FormattedView<ITEM>
    ): Array<string> => {
        const { dataView } = formattedView;
        const allIds: Array<string> = [];
        DV.forEach(dataView.dataSource.iter(), (item) =>
            allIds.push(dataView.dataSource.getId(item))
        );
        return allIds;
    };

    private row = memoizeOne((item: ITEM, formattedView: FormattedView<ITEM>) =>
        this.buildRow(item, formattedView)
    );

    private buildRow = (item: ITEM, formattedView: FormattedView<ITEM>) => {
        const { dataSource } = formattedView.dataView;
        const row: any = {};
        row[ITEM_FIELD] = item;
        row[ID_FIELD] = dataSource.getId(item);
        for (const col of formattedView.columns) {
            const { extractor } = col;
            row[extractor.id] = extractor.getValue(item);
        }
        return row;
    };

    private handleTableChange = (
        _pagination: PaginationConfig,
        _filters: Record<string, FilterValue | null>,
        sorter: SorterResult<any> | SorterResult<any>[]
    ) => {
        if (sorter === undefined) {
            return;
        }
        const { dataView } = this.props;
        // The user went back to the default sorting, thanks antd for signalling this so clearly

        if (Array.isArray(sorter)) {
            throw Error("`sorter` has unexpected type of array");
        }

        if (sorter.columnKey === undefined) {
            this.setState({
                sorter: this.getDefaultSorter(dataView),
            });
            return;
        }
        const currentSorter = this.sorter(this.state.sorter, dataView);
        if (
            currentSorter === undefined ||
            sorter.order === undefined ||
            currentSorter.extractorId !== sorter.columnKey ||
            (currentSorter.direction === "ASC" && sorter.order === "descend") ||
            (currentSorter.direction === "DESC" && sorter.order === "ascend")
        ) {
            this.setState({
                sorter: sorter.order
                    ? {
                          extractorId: sorter.columnKey.toString(),
                          direction: sorter.order === "ascend" ? "ASC" : "DESC",
                      }
                    : undefined,
            });
        }
    };

    private getDefaultSorter = (dataView: DV.DataView<ITEM>) => {
        return dataView.sorters.length > 0 ? dataView.sorters[0] : undefined;
    };

    // Memoize, but make it change when the activeRowId changes to trigger a
    // rerender of the ANT table
    private getRowClassName = memoizeOne(
        (activeRowId: string | undefined) =>
            (row: any): string => {
                const { dataView, rowClickHandler, getRowClassName } =
                    this.props;
                const item: ITEM = row[ITEM_FIELD];
                let isRowClickEnabled: boolean;
                if (!rowClickHandler) {
                    isRowClickEnabled = false;
                } else if (rowClickHandler.isClickEnabled) {
                    isRowClickEnabled = rowClickHandler.isClickEnabled(item);
                } else {
                    isRowClickEnabled = true;
                }
                const isActiveRow =
                    activeRowId !== undefined &&
                    dataView.dataSource.getId(item) === activeRowId;
                if (!isRowClickEnabled && !isActiveRow) {
                    return getRowClassName
                        ? getRowClassName(item, isActiveRow)
                        : "";
                } else {
                    return getRowClassName
                        ? getRowClassName(item, isActiveRow)
                        : `${isRowClickEnabled ? theme.clickableRow : ""} ${
                              isActiveRow ? theme.activeRow : ""
                          }`;
                }
            }
    );

    private getActiveRowId = () => {
        const { rowClickHandler } = this.props;
        return rowClickHandler &&
            rowClickHandler.type === "ActiveRowClickHandler"
            ? rowClickHandler.activeRowId
            : undefined;
    };

    private expandRow = (
        record: any,
        index: number,
        indent: number,
        expanded: boolean
    ): React.ReactNode => {
        const { expandedRowRender } = this.props;
        // This should never happen
        if (!expandedRowRender) {
            return null;
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        return expandedRowRender(record[ITEM_FIELD], index, indent, expanded);
    };

    private getExpandedRowClassName = (
        record: any,
        index: number,
        indent: number
    ): string => {
        const { expandedRowClassName } = this.props;
        if (!expandedRowClassName) {
            return "";
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        return expandedRowClassName(record[ITEM_FIELD], index, indent);
    };

    private handleOnRow = memoizeOne(
        (renderDataSource: Array<any>) => (row: any, rowIndex?: number) => {
            const { rowClickHandler } = this.props;
            if (!rowClickHandler) {
                throw new Error("No row click handler");
            }
            if (rowIndex === undefined) {
                throw new Error("rowIndex is undefined"); // Should never occur
            }
            const onClick = () => {
                const navigation: RowNavigation<ITEM> = {
                    rowIndex,
                    getRow: (rowIndex: number) => {
                        if (!this.currentDataSource) {
                            throw new Error("No data source");
                        }
                        if (this.currentDataSource !== renderDataSource) {
                            console.error(
                                "Data source changed, that must never happen!!!"
                            );
                        }
                        if (
                            !(
                                rowIndex >= 0 &&
                                rowIndex < this.currentDataSource.length
                            )
                        ) {
                            throw new Error(
                                "Data source index out of bound: " + rowIndex
                            );
                        }
                        return this.currentDataSource[rowIndex][ITEM_FIELD];
                    },
                };
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                rowClickHandler.onClick(row[ITEM_FIELD], navigation);
            };
            const result = {
                onClick,
            };
            return result;
        }
    );

    private ensureSelectionHasValidIds = () => {
        const { selectedRowIds } = this.state;
        const { dataView } = this.props;
        if (!selectedRowIds) {
            return;
        }
        const allIds = new Set<string>();
        const iter = dataView.dataSource.iter();
        DV.forEach(iter, (item) => allIds.add(dataView.dataSource.getId(item)));
        const newSelectedRowIds = selectedRowIds.filter((id) => allIds.has(id));
        if (selectedRowIds.length !== newSelectedRowIds.length) {
            this.setState({
                selectedRowIds: newSelectedRowIds,
            });
        }
    };

    private updateStyle = (): void => {
        const { id, mode, dataView, calcMinTableHeight } = this.props;
        if (!mode) {
            return;
        }

        const bodyWrapper = document.querySelector<HTMLElement>(
            `#${id} .ant-table-body`
        );
        if (!bodyWrapper) {
            console.warn("Couldn't find div element wrapping the body");
            return;
        }

        const hasData = !!dataView.dataSource.iter().next();

        // Keep the placeholder at the top
        if (hasData) {
            // Compute a series of styles to set on the body
            // We're not using the style attribute because removing and setting it causes the dom to recompute it's layout, which resets the scrollbar
            switch (mode.type) {
                case "MAX_HEIGHT": {
                    // The default style on the element has overflow: scroll, which
                    // displays a scrollbar when the height of the rows is less than
                    // max-height
                    if (mode.maxHeight > 0) {
                        bodyWrapper.style.maxHeight = `${mode.maxHeight}px`;
                    } else {
                        bodyWrapper.style.maxHeight = "";
                    }
                    break;
                }
                case "VIEWPORT": {
                    const minTableHeight = calcMinTableHeight
                        ? calcMinTableHeight()
                        : Math.round(window.innerHeight / 2);

                    const top =
                        bodyWrapper.getBoundingClientRect().top +
                        window.scrollY;
                    const newHeight = window.innerHeight - top;

                    if (newHeight > 0) {
                        bodyWrapper.style.height = `${newHeight}px`;
                    } else {
                        bodyWrapper.style.height = "";
                    }
                    if (minTableHeight > 0) {
                        bodyWrapper.style.minHeight = `${minTableHeight}px`;
                    } else {
                        bodyWrapper.style.minHeight = "";
                    }
                    break;
                }
                default: {
                    return assertNever(mode);
                }
            }
            bodyWrapper.style.overflow = `auto`;
        }

        // The header for the table lives in a separate table (which is necessary if you have a virtual list)
        // Unfortunately, antd-table-infinity attempts to make it as wide as the content of the table without
        // the scroll bar, and we need it to be as wide as the the table WITH the scrollbar.
        //
        // Antd-table-infinity achieves this by making the header scrollable
        // and hiding the scrollbar. This doesn't work properly in Firefox.
        //
        // In order to give the correct size to the header, we need to ensure that:
        // - The header has no scrollbar (done in the CSS)
        // - The header div gets some extra padding equivalent to the size of the scrollbar
        //
        // In order to known the width of the padding, we compute the width of the content with scrollbar,
        // the width of the content (without the scrollbar) and we compute the difference.
        const scrollbarHider = document.querySelector(
            `#${id} .ant-table-hide-scrollbar`
        );
        if (!scrollbarHider) {
            console.warn("Couldn't find scrollbar hider");
            return;
        }

        scrollbarHider.removeAttribute("style");

        if (hasData) {
            const bodyTable = document.querySelector(`#${id} .ant-table-body`);
            if (!bodyTable) {
                console.warn("Couldn't find table element containing the body");
                return;
            }
            const difference =
                bodyTable.getBoundingClientRect().width - bodyTable.clientWidth;

            scrollbarHider.setAttribute(
                "style",
                `padding-right: ${difference}px`
            );
        }
    };

    private handleVisibleColumnsChanged = (visibleColumnIds: Set<string>) => {
        this.setState({ visibleColumnIds });
        if (this.props.onVisibleColumnsChanged) {
            this.props.onVisibleColumnsChanged(visibleColumnIds);
        }
    };

    private resetSelection = () => {
        this.setState({
            selectedRowIds: [],
        });
    };
}

function getVisibleColumnsIds<ITEM extends object>(
    formattedView: FormattedView<ITEM>,
    visibleColumnIds: Set<string>
): Set<string> {
    if (visibleColumnIds.size > 0) {
        // Remove any id from visibleColumnIds which does not exist anymore
        const availableColumnIds = new Set<string>();
        formattedView.columns.forEach((col) => {
            switch (col.display) {
                case "ALWAYS":
                case "DEFAULT_VISIBLE":
                case "DEFAULT_HIDDEN":
                    availableColumnIds.add(col.extractor.id);
                    break;
                default:
                    break;
            }
        });
        availableColumnIds.forEach((colId) => {
            if (!visibleColumnIds.has(colId)) {
                availableColumnIds.delete(colId);
            }
        });
        if (availableColumnIds.size !== visibleColumnIds.size) {
            visibleColumnIds = availableColumnIds;
        }
    }
    // We'll run through this block if visibleColumnIds is not initialized or if we emptied it previously
    if (visibleColumnIds.size === 0) {
        visibleColumnIds = new Set<string>();
        formattedView.columns.forEach((col) => {
            switch (col.display) {
                case "ALWAYS":
                case "DEFAULT_VISIBLE":
                    visibleColumnIds.add(col.extractor.id);
                    break;
                default:
                    break;
            }
        });
    }
    return visibleColumnIds;
}

interface DataTableProps<ITEM> {
    id: string;
    dataView: DV.DataView<ITEM>;
    columnBuilders?: { [extractorId: string]: ColumnBuilder<ITEM, any> };
    rowClickHandler?: RowClickHandler<ITEM>;
    selectionHandler?: SelectionHandler<ITEM>;
    expandedRowRender?: (
        item: ITEM,
        index: number,
        indent: number,
        expanded: boolean
    ) => React.ReactNode;
    expandedRowClassName?: (
        item: ITEM,
        index: number,
        indent: number
    ) => string;
    /*
     * - `undefined` => a simple AntD Table, not virtual in any way.
     * - { type: "MAX_HEIGHT", .. } => a virtual table with a real maximum height.
     * - { type: "VIEWPORT", .. } => a virtual table which is intended to appear on the screen all the time.
     *   That merely means the top of the box is always visible, and the bottom is computed to be at the
     *   bottom of the page.
     */
    mode?: DataTableMode;
    tools?: Array<StandardToolSpec<ITEM>>;
    footerRenderer?: () => JSX.Element;
    hidePlaceholder?: boolean;
    getRowClassName?: (item: ITEM, isActiveRow: boolean) => string;
    calcMinTableHeight?: () => number;
    onVisibleColumnsChanged?: (visibleColumnIds: Set<string>) => void;
    visibleColumnIds?: Set<string> | null;
    getRowCountLabel?:
        | ((pageSize: number, totalRowCount: number) => string)
        | null;
    getSinglePageRowCountLabel?: (totalRowCount: number) => string;
    paginationVariant?: RowCountLabelVariant;
}

type DataTableMode =
    | {
          type: "MAX_HEIGHT";
          maxHeight: number;
      }
    | {
          type: "VIEWPORT";
      };

const DATA_TABLE_MODE_VIEWPORT: DataTableMode = { type: "VIEWPORT" };

interface DataTableState {
    sorter: DV.Sorter | undefined;
    selectedRowIds: Array<string>;
    visibleColumnIds: Set<string>;
}
export type {
    DataTableProps,
    DataTableMode,
    FormattedView,
    SimpleColumn,
    ColumnBuilder,
    FixedWidthLayout,
    Layout,
    Display,
    SelectionToolSpec,
    StandardToolSpec,
    CellClassName,
    ColumnOptions,
    RowClickHandler,
    SimpleRowClickHandler,
    ActiveRowClickHandler,
    SelectionHandler,
};
export {
    DataTable,
    DATA_TABLE_MODE_VIEWPORT,
    toColumn,
    sortColumns,
    buildStandardTools,
    buildSelectionExportTool,
    buildStandardExportTool,
    buildToolbarWithViewAdjuster,
    getVisibleColumnsIds,
    DEFAULT_COLUMN_BUILDER_ID,
};
